/**
 * Error Recovery Utilities
 * Provides recovery mechanisms for common Expense errors
 */

import { handleError, handleApiError, EXPENSE_ERROR_CODES } from "./errorHandling";
import { toast } from "react-toastify";

/**
 * Recovery strategies for different error types
 */
export const RECOVERY_STRATEGIES = {
  RETRY: "retry",
  REFRESH: "refresh",
  RESET_FORM: "reset_form",
  CLEAR_CACHE: "clear_cache",
  REDIRECT: "redirect",
  IGNORE: "ignore",
};

/**
 * Attempt to recover from an error
 * @param {Error|string} error - The error
 * @param {string} context - Error context
 * @param {Object} options - Recovery options
 * @returns {Promise<boolean>} True if recovery was successful
 */
export const attemptRecovery = async (error, context, options = {}) => {
  const {
    strategy = RECOVERY_STRATEGIES.RETRY,
    maxRetries = 3,
    retryDelay = 1000,
    onRetry,
    onRefresh,
    onResetForm,
    onClearCache,
    onRedirect,
  } = options;

  const errorInfo = handleError(error, context);

  switch (strategy) {
    case RECOVERY_STRATEGIES.RETRY:
      if (onRetry && maxRetries > 0) {
        for (let i = 0; i < maxRetries; i++) {
          try {
            await new Promise((resolve) => setTimeout(resolve, retryDelay * (i + 1)));
            await onRetry();
            toast.success("Operation retried successfully", {
              position: "bottom-right",
              autoClose: 2000,
            });
            return true;
          } catch (retryError) {
            if (i === maxRetries - 1) {
              handleError(retryError, `${context} (retry ${i + 1})`);
            }
          }
        }
      }
      return false;

    case RECOVERY_STRATEGIES.REFRESH:
      if (onRefresh) {
        try {
          await onRefresh();
          toast.info("Data refreshed", {
            position: "bottom-right",
            autoClose: 2000,
          });
          return true;
        } catch (refreshError) {
          handleError(refreshError, `${context} (refresh)`);
          return false;
        }
      }
      return false;

    case RECOVERY_STRATEGIES.RESET_FORM:
      if (onResetForm) {
        try {
          onResetForm();
          toast.info("Form reset", {
            position: "bottom-right",
            autoClose: 2000,
          });
          return true;
        } catch (resetError) {
          handleError(resetError, `${context} (reset form)`);
          return false;
        }
      }
      return false;

    case RECOVERY_STRATEGIES.CLEAR_CACHE:
      if (onClearCache) {
        try {
          onClearCache();
          toast.info("Cache cleared", {
            position: "bottom-right",
            autoClose: 2000,
          });
          return true;
        } catch (clearError) {
          handleError(clearError, `${context} (clear cache)`);
          return false;
        }
      }
      return false;

    case RECOVERY_STRATEGIES.REDIRECT:
      if (onRedirect) {
        try {
          onRedirect();
          return true;
        } catch (redirectError) {
          handleError(redirectError, `${context} (redirect)`);
          return false;
        }
      }
      return false;

    case RECOVERY_STRATEGIES.IGNORE:
      return true;

    default:
      return false;
  }
};

/**
 * Get recovery strategy for specific error code
 * @param {string} errorCode - Error code
 * @returns {string} Recovery strategy
 */
export const getRecoveryStrategy = (errorCode) => {
  switch (errorCode) {
    case EXPENSE_ERROR_CODES.NETWORK_ERROR:
    case EXPENSE_ERROR_CODES.TIMEOUT_ERROR:
      return RECOVERY_STRATEGIES.RETRY;

    case EXPENSE_ERROR_CODES.DATA_FETCH_ERROR:
      return RECOVERY_STRATEGIES.REFRESH;

    case EXPENSE_ERROR_CODES.API_VALIDATION:
    case EXPENSE_ERROR_CODES.MISSING_REQUIRED_FIELD:
    case EXPENSE_ERROR_CODES.INVALID_DATE:
    case EXPENSE_ERROR_CODES.INVALID_AMOUNT:
      return RECOVERY_STRATEGIES.RESET_FORM;

    case EXPENSE_ERROR_CODES.DATA_PARSE_ERROR:
    case EXPENSE_ERROR_CODES.DATA_SAVE_ERROR:
      return RECOVERY_STRATEGIES.CLEAR_CACHE;

    case EXPENSE_ERROR_CODES.API_UNAUTHORIZED:
      return RECOVERY_STRATEGIES.REDIRECT;

    case EXPENSE_ERROR_CODES.FILE_SIZE_EXCEEDED:
    case EXPENSE_ERROR_CODES.FILE_COUNT_EXCEEDED:
      return RECOVERY_STRATEGIES.IGNORE;

    default:
      return RECOVERY_STRATEGIES.RETRY;
  }
};

/**
 * Handle error with automatic recovery
 * @param {Error|string} error - The error
 * @param {string} context - Error context
 * @param {Object} options - Recovery options
 * @returns {Promise<Object>} Recovery result
 */
export const handleErrorWithRecovery = async (error, context, options = {}) => {
  const errorInfo = handleError(error, context);
  const strategy = options.strategy || getRecoveryStrategy(errorInfo.code);

  const recoveryResult = await attemptRecovery(error, context, {
    ...options,
    strategy,
  });

  return {
    ...errorInfo,
    recovered: recoveryResult,
    strategy,
  };
};

/**
 * Create a retry wrapper for async functions
 * @param {Function} asyncFn - Async function to wrap
 * @param {Object} options - Retry options
 * @returns {Function} Wrapped function with retry logic
 */
export const withRetry = (asyncFn, options = {}) => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    onRetry,
    shouldRetry = (error) => {
      const errorInfo = handleError(error, "retry check");
      return (
        errorInfo.code === EXPENSE_ERROR_CODES.NETWORK_ERROR ||
        errorInfo.code === EXPENSE_ERROR_CODES.TIMEOUT_ERROR ||
        errorInfo.code === EXPENSE_ERROR_CODES.API_SERVER_ERROR
      );
    },
  } = options;

  return async (...args) => {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0 && onRetry) {
          await onRetry(attempt);
        }

        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));
        }

        return await asyncFn(...args);
      } catch (error) {
        lastError = error;

        if (attempt < maxRetries && shouldRetry(error)) {
          continue;
        }

        throw error;
      }
    }

    throw lastError;
  };
};
